import {
  ItemScopeEnum,
  type AbutmentCharacteristics,
  type DeepPartial,
  type ImplantCharacteristics,
  type ImplantedItemCharacteristics,
  type Product,
  type ShadeCharacteristics,
} from "@jasper/client-models";
import type { OrderProductFieldsFragment } from "../../gql/generated/graphql";

type OrderProductItem = OrderProductFieldsFragment["items"][number];

type RawShadeCharacteristics = NonNullable<
  NonNullable<
    OrderProductItem["materialCharacteristics"]
  >["shadeCharacteristics"]
>;

export type MaterialLabel = { name: string };
// colorCode is the shade alone (« A3 »), name also carries its guide (« A3 (Vita Classic) »)
export type ShadeLabel = { name: string; colorCode?: string };

// Names of the materials and shades that formatted products only reference by id
export type MaterialAndShadeCatalog = {
  materials: Record<string, MaterialLabel>;
  shades: Record<string, ShadeLabel>;
};

const formatImplantCharacteristics = (
  implantCharacteristics: OrderProductItem["implantCharacteristics"]
): ImplantCharacteristics | undefined => {
  if (
    implantCharacteristics?.__typename ===
    "ReferencedImplantCharacteristicsModel"
  ) {
    return {
      implantReferenceId: implantCharacteristics.implantReference.id,
      implantPlatform: implantCharacteristics.implantPlatform,
      scanBodyReference: implantCharacteristics.scanBodyReference,
    };
  }

  if (
    implantCharacteristics?.__typename ===
    "UnreferencedImplantCharacteristicsModel"
  ) {
    return {
      implantBrand: implantCharacteristics.implantBrand,
      implantSystem: implantCharacteristics.implantSystem,
      implantPlatform: implantCharacteristics.implantPlatform,
      scanBodyReference: implantCharacteristics.scanBodyReference,
    };
  }

  return undefined;
};

const formatAbutmentCharacteristics = (
  abutmentCharacteristics: OrderProductItem["abutmentCharacteristics"]
): AbutmentCharacteristics | undefined => {
  if (
    abutmentCharacteristics?.__typename ===
    "StandardAbutmentCharacteristicsModel"
  ) {
    return {
      abutmentType: abutmentCharacteristics.abutmentType,
      abutmentPreferences:
        abutmentCharacteristics.abutmentPreferences ?? undefined,
    };
  }

  if (
    abutmentCharacteristics?.__typename === "CustomAbutmentCharacteristicsModel"
  ) {
    return {
      abutmentType: abutmentCharacteristics.abutmentType,
    };
  }

  return undefined;
};

const formatImplantedItemCharacteristics = (
  implantedItemCharacteristics: OrderProductItem["implantedItemCharacteristics"]
): ImplantedItemCharacteristics | undefined => {
  if (!implantedItemCharacteristics?.retentionType) {
    return undefined;
  }

  return {
    retentionType: implantedItemCharacteristics.retentionType,
  };
};

const formatShadeCharacteristics = (
  rawShadeCharacteristics: RawShadeCharacteristics | null | undefined
): ShadeCharacteristics | undefined => {
  if (
    rawShadeCharacteristics?.__typename === "MultiShadeCharacteristicsModel"
  ) {
    return {
      isMultiShade: true,
      shadeType: rawShadeCharacteristics.baseShade.shadeType,
      baseShade: rawShadeCharacteristics.baseShade.id,
      gingivalShade: rawShadeCharacteristics.gingivalShade.id,
      incisalShade: rawShadeCharacteristics.incisalShade.id,
    };
  }

  if (
    rawShadeCharacteristics?.__typename === "SingleShadeCharacteristicsModel"
  ) {
    return {
      isMultiShade: false,
      shadeType: rawShadeCharacteristics.shade.shadeType,
      shade: rawShadeCharacteristics.shade.id,
    };
  }

  return undefined;
};

const formatItem = (item: OrderProductItem) => ({
  id: item.id,
  type: item.type,
  /*
    Keep the scope discrimination the schema now carries: spreading one branch
    or the other makes the mapped item a union, so consumers can narrow it.
  */
  ...("arch" in item
    ? { scope: ItemScopeEnum.arch, arch: item.arch }
    : { scope: ItemScopeEnum.tooth, tooth: item.tooth }),
  inlayCoreCharacteristics: item.inlayCoreCharacteristics
    ? {
        hasClavette: item.inlayCoreCharacteristics.hasClavette,
        scanPost: item.inlayCoreCharacteristics.scanPost,
      }
    : undefined,
  materialCharacteristics: item.materialCharacteristics
    ? {
        material: item.materialCharacteristics.material.id,
        shadeCharacteristics: formatShadeCharacteristics(
          item.materialCharacteristics.shadeCharacteristics
        ),
      }
    : undefined,
  waxUpType: item.waxUpType ?? undefined,
  implantCharacteristics: formatImplantCharacteristics(
    item.implantCharacteristics
  ),
  abutmentCharacteristics: formatAbutmentCharacteristics(
    item.abutmentCharacteristics
  ),
  implantedItemCharacteristics: formatImplantedItemCharacteristics(
    item.implantedItemCharacteristics
  ),
});

// The only place turning a GraphQL product into the client model: every query requests OrderProductFields for it
export const formatProduct = (product: OrderProductFieldsFragment) =>
  ({
    id: product.id,
    teeth: product.teeth,
    type: product.type,
    itemPreferences: product.itemPreferences,
    items: product.items.map(formatItem),
  }) satisfies DeepPartial<Product>;

// `__typename` is optional in the generated types: the shape tells the two variants apart
const getShades = (shadeCharacteristics: RawShadeCharacteristics) =>
  "shade" in shadeCharacteristics
    ? [shadeCharacteristics.shade]
    : [
        shadeCharacteristics.gingivalShade,
        shadeCharacteristics.baseShade,
        shadeCharacteristics.incisalShade,
      ];

// Built from the same response as the formatted products, so it holds every id they reference
export const buildMaterialAndShadeCatalog = (
  products: ReadonlyArray<OrderProductFieldsFragment>
): MaterialAndShadeCatalog => {
  const materialAndShadeCatalog: MaterialAndShadeCatalog = {
    materials: {},
    shades: {},
  };

  products
    .flatMap(({ items }) => items)
    .forEach(({ materialCharacteristics }) => {
      if (!materialCharacteristics) {
        return;
      }
      const { material, shadeCharacteristics } = materialCharacteristics;

      materialAndShadeCatalog.materials[material.id] = { name: material.name };
      if (shadeCharacteristics) {
        getShades(shadeCharacteristics).forEach(({ id, name, colorCode }) => {
          materialAndShadeCatalog.shades[id] = {
            name,
            colorCode: colorCode ?? undefined,
          };
        });
      }
    });

  return materialAndShadeCatalog;
};

// For pages loading products without useOrderById (order lists)
export const formatProducts = (
  products: ReadonlyArray<OrderProductFieldsFragment>
) => ({
  products: products.map(formatProduct),
  materialAndShadeCatalog: buildMaterialAndShadeCatalog(products),
});
